import * as React from 'react';
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import Button from 'antd/lib/button';
import Flex from 'antd/lib/flex';
import Form from 'antd/lib/form';
import InputNumber from 'antd/lib/input-number';
import Select from 'antd/lib/select';
import Space from 'antd/lib/space';
import Tooltip from 'antd/lib/tooltip';
import Text from 'antd/lib/typography/Text';
import { LifecycleEventType, RelativeTiming, ReminderOffset } from '@/types';
import { displayLifecycleEventName, relativeTimingLabel } from '@/helpers/displayHelpers';
import { runwayColors } from '@/components/styles/globalStyles';

export const REMINDER_OFFSETS_FIELD = 'reminderOffsets';

const MAX_REMINDER_OFFSETS = 10;

const DURATION_UNITS: { key: 'days' | 'hours' | 'minutes'; suffix: string }[] = [
    { key: 'days', suffix: 'd' },
    { key: 'hours', suffix: 'h' },
    { key: 'minutes', suffix: 'm' },
];

const timingOptions = [
    { value: RelativeTiming.before, label: relativeTimingLabel(RelativeTiming.before) },
    { value: RelativeTiming.onTime, label: relativeTimingLabel(RelativeTiming.onTime) },
];

const normalizeToZero = (value: number | null | undefined) => (value === null || value === undefined ? 0 : value);

interface ReminderOffsetsSettingsProps {
    event: LifecycleEventType;
}

export const ReminderOffsetsSettings: React.FC<ReminderOffsetsSettingsProps> = ({ event }) => {
    const form = Form.useFormInstance();
    const offsets = Form.useWatch<ReminderOffset[]>(REMINDER_OFFSETS_FIELD, form);
    const eventLabel = displayLifecycleEventName(event).toLowerCase();

    return (
        <Form.List name={REMINDER_OFFSETS_FIELD}>
            {(rows, { add, remove }) => {
                const isLastRow = rows.length <= 1;
                return (
                    <Space direction="vertical" style={{ width: '100%' }} size={12}>
                        {rows.map(({ key, name }) => {
                            const isOnTime = offsets?.[name]?.timing === RelativeTiming.onTime;
                            return (
                                <Flex
                                    key={key}
                                    align="center"
                                    justify="space-between"
                                    gap={8}
                                    wrap="wrap"
                                    style={{ width: '100%' }}
                                >
                                    <Flex align="center" gap={8} wrap="wrap">
                                        {DURATION_UNITS.map(({ key: unit, suffix }) => (
                                            <Form.Item
                                                key={unit}
                                                name={[name, unit]}
                                                normalize={normalizeToZero}
                                                style={{ marginBottom: 0 }}
                                            >
                                                <InputNumber
                                                    min={0}
                                                    precision={0}
                                                    disabled={isOnTime}
                                                    style={numberStyle}
                                                    suffix={<Text type="secondary">{suffix}</Text>}
                                                />
                                            </Form.Item>
                                        ))}
                                        <Form.Item name={[name, 'timing']} style={{ marginBottom: 0, minWidth: 130 }}>
                                            <Select
                                                options={timingOptions}
                                                onChange={(value) => {
                                                    if (value === RelativeTiming.onTime) {
                                                        DURATION_UNITS.forEach(({ key: unit }) =>
                                                            form.setFieldValue([REMINDER_OFFSETS_FIELD, name, unit], 0),
                                                        );
                                                    }
                                                }}
                                            />
                                        </Form.Item>
                                        <Text>{eventLabel}</Text>
                                    </Flex>
                                    <Tooltip title={isLastRow ? 'At least one reminder must be scheduled' : undefined}>
                                        <span>
                                            <Button
                                                icon={
                                                    <DeleteOutlined
                                                        style={{
                                                            color: isLastRow ? undefined : runwayColors.statusRed,
                                                        }}
                                                    />
                                                }
                                                danger
                                                type="link"
                                                disabled={isLastRow}
                                                onClick={() => remove(name)}
                                            />
                                        </span>
                                    </Tooltip>
                                </Flex>
                            );
                        })}
                        <Tooltip
                            title={
                                rows.length >= MAX_REMINDER_OFFSETS ?
                                    `At most ${MAX_REMINDER_OFFSETS} reminders can be scheduled`
                                :   undefined
                            }
                        >
                            <Button
                                icon={<PlusOutlined style={{ color: runwayColors.primary }} />}
                                type="link"
                                style={{ paddingLeft: 0 }}
                                disabled={rows.length >= MAX_REMINDER_OFFSETS}
                                onClick={() => add({ days: 0, hours: 0, minutes: 30, timing: RelativeTiming.before })}
                            >
                                Add another reminder notification
                            </Button>
                        </Tooltip>
                    </Space>
                );
            }}
        </Form.List>
    );
};

const numberStyle: React.CSSProperties = { width: 70, textAlign: 'center', borderRadius: 8 };
